import React, { useState, useEffect, useCallback } from 'react';
import { XMarkIcon, UserPlusIcon, EnvelopeIcon, AtSymbolIcon, PencilIcon, EyeIcon, UsersIcon, TrashIcon, ChevronDownIcon, ClockIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { CollectionSharingService, CollectionsService, type CollectionInvitationResponse } from '../../services/api';
import { toast } from 'react-hot-toast';
import { ConfirmationModal } from '../common/ConfirmationModal';

interface CollectionSharingModalProps {
  collectionId: string;
  collectionName: string;
  isOpen: boolean;
  onClose: () => void;
  onInviteSent?: () => void;
}

type PermissionLevel = 'read' | 'write' | 'admin';

interface SharedUser {
  userId: string;
  username: string;
  email?: string;
  permissionLevel: PermissionLevel;
}

// Server-side invitation interface is imported from API types

export const CollectionSharingModal: React.FC<CollectionSharingModalProps> = ({
  collectionId,
  collectionName,
  isOpen,
  onClose,
  onInviteSent
}) => {
  const [inviteMethod, setInviteMethod] = useState<'email' | 'username'>('email');
  const [inviteValue, setInviteValue] = useState('');
  const [permission, setPermission] = useState<PermissionLevel>('read');
  const [isLoading, setIsLoading] = useState(false);
  
  // Shared users management
  const [sharedUsers, setSharedUsers] = useState<SharedUser[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [selectedPermission, setSelectedPermission] = useState<PermissionLevel>('read');
  
  // Server-side pending invitations
  const [pendingInvitations, setPendingInvitations] = useState<CollectionInvitationResponse[]>([]);
  const [isLoadingInvitations, setIsLoadingInvitations] = useState(false);
  
  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({ isOpen: false, title: '', message: '', onConfirm: () => {} });

  const handleInvite = async () => {
    if (!inviteValue.trim()) {
      toast.error('Please enter an email or username');
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        permission,
        ...(inviteMethod === 'email' ? { email: inviteValue } : { username: inviteValue })
      };

      await CollectionSharingService.inviteUserToCollection({
        collectionId,
        requestBody: payload
      });

      toast.success(`Invitation sent to ${inviteValue}`);
      setInviteValue('');
      
      // Refresh both shared users and invitations lists
      await fetchSharedUsers();
      await fetchPendingInvitations();
      onInviteSent?.();
    } catch (error: unknown) {
      const errorObj = error as Record<string, unknown>;
      const body = errorObj?.body as Record<string, unknown> | undefined;
      const message = (body?.message as string) || (errorObj?.message as string) || 'Failed to send invitation';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch current shared users
  const fetchSharedUsers = useCallback(async () => {
    if (!isOpen) return;
    
    try {
      setIsLoadingUsers(true);
      const response = await CollectionsService.getCollectionPermissions({ collectionId });
      
      const users: SharedUser[] = (response.permissions || []).map((p: unknown) => {
        const permission = p as Record<string, unknown>;
        const user = permission.user as Record<string, unknown> | undefined;
        
        // Try multiple possible userId fields from the API response
        const userId = (permission.userId as string) || 
                      (permission.user_id as string) || 
                      (user?.id as string) || 
                      (user?.userId as string) || 
                      (user?.user_id as string);
                      
        console.log('Permission object:', permission);
        console.log('Extracted userId:', userId);
        
        if (!userId) {
          console.warn('No userId found in permission:', permission);
        }
        
        return {
          userId: userId || '',
          username: (permission.username as string) || (user?.username as string) || 'Unknown',
          email: (permission.email as string) || (user?.email as string),
          permissionLevel: (permission.permissionLevel as PermissionLevel) || (permission.permission as PermissionLevel) || 'read'
        };
      });
      
      setSharedUsers(users);
    } catch (error) {
      console.error('Failed to fetch shared users:', error);
      setSharedUsers([]);
    } finally {
      setIsLoadingUsers(false);
    }
  }, [collectionId, isOpen]);

  // Fetch pending invitations
  const fetchPendingInvitations = useCallback(async () => {
    if (!isOpen) return;
    
    try {
      setIsLoadingInvitations(true);
      const invitations = await CollectionSharingService.listCollectionInvitations({
        collectionId,
        status: 'pending'
      });
      
      setPendingInvitations(invitations);
    } catch (error) {
      console.error('Failed to fetch pending invitations:', error);
      setPendingInvitations([]);
    } finally {
      setIsLoadingInvitations(false);
    }
  }, [collectionId, isOpen]);

  // Update user permission
  const handleUpdatePermission = async (userId: string, newPermission: PermissionLevel) => {
    try {
      console.log('Updating permission for userId:', userId, 'to level:', newPermission);
      
      // Find the user to get their available identifiers
      const user = sharedUsers.find(u => u.userId === userId);
      if (!user) {
        toast.error('User not found');
        return;
      }

      // Build request body with available identifiers (prioritize email > username > userId)
      const requestBody: {
        permissionLevel: PermissionLevel;
        email?: string;
        username?: string;
        userId?: string;
      } = {
        permissionLevel: newPermission
      };

      // Prioritize email if available
      if (user.email && user.email.trim()) {
        requestBody.email = user.email;
      } 
      // Fall back to username if no email but username exists
      else if (user.username && user.username.trim()) {
        requestBody.username = user.username;
      }
      // Fall back to userId as last resort
      else {
        requestBody.userId = userId;
      }

      console.log('Request body:', requestBody);

      // Use the generated API client with the updated types
      await CollectionsService.grantCollectionPermission({
        collectionId,
        requestBody
      });
      
      toast.success('Permission updated successfully');
      setSharedUsers(prev => 
        prev.map(user => 
          user.userId === userId ? { ...user, permissionLevel: newPermission } : user
        )
      );
      setEditingUserId(null);
    } catch (error: unknown) {
      console.error('Permission update error:', error);
      const errorObj = error as Record<string, unknown>;
      const body = errorObj?.body as Record<string, unknown> | undefined;
      const message = (body?.message as string) || (errorObj?.message as string) || 'Failed to update permission';
      toast.error(message);
    }
  };

  // Remove user from collection
  const handleRemoveUser = (userId: string, username: string) => {
    console.log('Attempting to remove user:', { userId, username });
    
    if (!userId || userId.trim() === '') {
      toast.error('Cannot remove user: Invalid user ID');
      return;
    }
    
    setConfirmModal({
      isOpen: true,
      title: 'Remove User Access',
      message: `Remove ${username}'s access to this collection?`,
      onConfirm: async () => {
        try {
          console.log('Revoking permission for userId:', userId);
          await CollectionsService.revokeCollectionPermission({
            collectionId,
            userId
          });
          
          toast.success('Access revoked successfully');
          setSharedUsers(prev => prev.filter(user => user.userId !== userId));
          onInviteSent?.(); // Refresh the sharing indicator
        } catch (error: unknown) {
          console.error('Failed to revoke permission:', error);
          const errorObj = error as Record<string, unknown>;
          const body = errorObj?.body as Record<string, unknown> | undefined;
          const message = (body?.message as string) || 'Failed to revoke access';
          toast.error(message);
        }
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
      }
    });
  };

  // Revoke pending invitation
  const handleRevokeInvitation = (invitationId: string, inviteeEmail: string) => {
    setConfirmModal({
      isOpen: true,
      title: 'Revoke Invitation',
      message: `Revoke invitation for ${inviteeEmail}?`,
      onConfirm: async () => {
        try {
          await CollectionSharingService.revokeCollectionInvitation({
            collectionId,
            invitationToken: invitationId
          });
          
          toast.success('Invitation revoked successfully');
          // Refresh the invitations list from server
          await fetchPendingInvitations();
          onInviteSent?.(); // Refresh the sharing indicator
        } catch (error: unknown) {
          console.error('Failed to revoke invitation:', error);
          const errorObj = error as Record<string, unknown>;
          const body = errorObj?.body as Record<string, unknown> | undefined;
          const message = (body?.message as string) || 'Failed to revoke invitation';
          toast.error(message);
        }
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
      }
    });
  };

  // Fetch shared users and pending invitations when modal opens
  useEffect(() => {
    void fetchSharedUsers();
    void fetchPendingInvitations();
  }, [fetchSharedUsers, fetchPendingInvitations]);

  if (!isOpen) return null;

  // Helper functions for permission display
  const getPermissionIcon = (level: PermissionLevel) => {
    switch (level) {
      case 'admin': return UsersIcon;
      case 'write': return PencilIcon;
      case 'read': return EyeIcon;
    }
  };

  const getTimeRemaining = (expiresAt: string) => {
    if (!expiresAt) return 'No expiration';
    
    const expirationDate = new Date(expiresAt);
    const now = new Date();
    const diff = expirationDate.getTime() - now.getTime();
    
    if (diff <= 0) return 'Expired';
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) return `${days} day${days > 1 ? 's' : ''} remaining`;
    return `${hours} hour${hours > 1 ? 's' : ''} remaining`;
  };

  const getPermissionLabel = (level: PermissionLevel) => {
    switch (level) {
      case 'admin': return 'Admin';
      case 'write': return 'Editor';
      case 'read': return 'View Only';
    }
  };

  const getPermissionColor = (level: PermissionLevel) => {
    switch (level) {
      case 'admin': return 'text-purple-600 dark:text-purple-400';
      case 'write': return 'text-green-600 dark:text-green-400';
      case 'read': return 'text-blue-600 dark:text-blue-400';
    }
  };

  const permissionOptions = [
    {
      value: 'read' as PermissionLevel,
      label: 'View Only',
      description: 'Can view queries and run them',
      icon: EyeIcon,
      color: 'text-blue-500'
    },
    {
      value: 'write' as PermissionLevel,
      label: 'Editor',
      description: 'Can create, edit, and delete queries',
      icon: PencilIcon,
      color: 'text-green-500'
    },
    {
      value: 'admin' as PermissionLevel,
      label: 'Admin',
      description: 'Full control including sharing and permissions',
      icon: UsersIcon,
      color: 'text-purple-500'
    }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Share Collection
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {collectionName}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Current Access Section */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <UsersIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Current Access
            </h3>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              ({sharedUsers.length} {sharedUsers.length === 1 ? 'user' : 'users'})
            </span>
          </div>

          {isLoadingUsers ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : sharedUsers.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <UsersIcon className="w-12 h-12 text-gray-400 dark:text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500 dark:text-gray-400">
                This collection is not shared with anyone yet
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {sharedUsers.map((user) => {
                const Icon = getPermissionIcon(user.permissionLevel);
                const isEditing = editingUserId === user.userId;
                
                return (
                  <div
                    key={user.userId}
                    className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center">
                        <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                          {user.username.substring(0, 2).toUpperCase()}
                        </span>
                      </div>
                      
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white">
                          {user.username}
                        </div>
                        {user.email && (
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {user.email}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {isEditing ? (
                        <select
                          value={selectedPermission}
                          onChange={(e) => {
                            const val = e.target.value
                            const allowed: PermissionLevel[] = ['read','write','admin']
                            const newPermission: PermissionLevel = allowed.includes(val as PermissionLevel) ? (val as PermissionLevel) : 'read'
                            setSelectedPermission(newPermission);
                            handleUpdatePermission(user.userId, newPermission);
                          }}
                          onBlur={() => { setEditingUserId(null) }}
                          className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-sm focus:ring-2 focus:ring-blue-500"
                          autoFocus
                        >
                          <option value="read">View Only</option>
                          <option value="write">Editor</option>
                          <option value="admin">Admin</option>
                        </select>
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            setEditingUserId(user.userId);
                            setSelectedPermission(user.permissionLevel);
                          }}
                          className={`flex items-center gap-1 px-3 py-1 rounded-md border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 ${getPermissionColor(user.permissionLevel)}`}
                        >
                          <Icon className="w-4 h-4" />
                          <span className="text-sm font-medium">
                            {getPermissionLabel(user.permissionLevel)}
                          </span>
                          <ChevronDownIcon className="w-3.5 h-3.5" />
                        </button>
                      )}
                      
                      <button
                        type="button"
                        onClick={async () => handleRemoveUser(user.userId, user.username)}
                        className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md"
                        title="Remove access"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Pending Invitations Section */}
        {(isLoadingInvitations || pendingInvitations.length > 0) && (
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <ClockIcon className="w-5 h-5 text-orange-500 dark:text-orange-400" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Pending Invitations
              </h3>
              {!isLoadingInvitations && (
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  ({pendingInvitations.length} {pendingInvitations.length === 1 ? 'invitation' : 'invitations'})
                </span>
              )}
            </div>

            {isLoadingInvitations ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <div className="space-y-2">
                {pendingInvitations.map((invitation) => {
                  const isPerm = (val: unknown): val is PermissionLevel => typeof val === 'string' && ['read','write','admin'].includes(val)
                  const perm: PermissionLevel = isPerm(invitation.permission) ? invitation.permission : 'read'
                  const Icon = getPermissionIcon(perm);
                  const timeRemaining = getTimeRemaining(invitation.expires_at || '');
                  const isExpired = timeRemaining === 'Expired';
                  
                  return (
                    <div
                      key={invitation.id}
                      className={`flex items-center justify-between p-3 border rounded-lg ${
                        isExpired 
                          ? 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20'
                          : 'border-orange-200 dark:border-orange-700 bg-orange-50 dark:bg-orange-900/20'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          isExpired 
                            ? 'bg-red-200 dark:bg-red-800' 
                            : 'bg-orange-200 dark:bg-orange-700'
                        }`}>
                          <EnvelopeIcon className={`w-5 h-5 ${
                            isExpired 
                              ? 'text-red-600 dark:text-red-400' 
                              : 'text-orange-600 dark:text-orange-400'
                          }`} />
                        </div>
                        
                        <div>
                          <div className="font-medium text-gray-900 dark:text-white">
                            {invitation.invitee_email}
                          </div>
                          <div className={`text-sm flex items-center gap-2 ${
                            isExpired 
                              ? 'text-red-600 dark:text-red-400' 
                              : 'text-orange-600 dark:text-orange-400'
                          }`}>
                            <ClockIcon className="w-3.5 h-3.5" />
                            {timeRemaining}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {/* Permission Badge */}
                        <div className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ${
                          isExpired 
                            ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400' 
                            : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600'
                        }`}>
                          <Icon className="w-3 h-3" />
                          {getPermissionLabel(perm)}
                        </div>
                        
                        {/* Status Badge */}
                        <div className={`px-2 py-1 rounded-md text-xs font-medium capitalize ${
                          invitation.status === 'pending' && !isExpired
                            ? 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-400'
                            : invitation.status === 'expired' || isExpired
                            ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                        }`}>
                          {isExpired ? 'expired' : invitation.status}
                        </div>
                        
                        {/* Revoke Button */}
                        {!isExpired && invitation.status === 'pending' && (
                          <button
                            type="button"
                            onClick={() => handleRevokeInvitation(invitation.id || '', invitation.invitee_email || '')}
                            className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md"
                            title="Revoke invitation"
                          >
                            <XCircleIcon className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Invite New Users Section */}
        <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            Invite New Users
          </h3>
          
          <div className="space-y-4">
            {/* Invite Method Toggle */}
          <div className="flex rounded-lg bg-gray-100 dark:bg-gray-700 p-1">
            <button
              type="button"
              onClick={() => { setInviteMethod('email') }}
              className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md transition-colors ${
                inviteMethod === 'email'
                  ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400'
              }`}
            >
              <EnvelopeIcon className="w-4 h-4" />
              Email
            </button>
            <button
              type="button"
              onClick={() => { setInviteMethod('username') }}
              className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md transition-colors ${
                inviteMethod === 'username'
                  ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400'
              }`}
            >
              <AtSymbolIcon className="w-4 h-4" />
              Username
            </button>
          </div>

          {/* Input Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {inviteMethod === 'email' ? 'Email Address' : 'Username'}
            </label>
            <input
              type={inviteMethod === 'email' ? 'email' : 'text'}
              value={inviteValue}
              onChange={(e) => { setInviteValue(e.target.value) }}
              placeholder={inviteMethod === 'email' ? 'user@example.com' : 'john_doe'}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Permission Level */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Permission Level
            </label>
            <div className="space-y-2">
              {permissionOptions.map((option) => {
                const Icon = option.icon;
                return (
                  <label
                    key={option.value}
                    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      permission === option.value
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <input
                      type="radio"
                      name="permission"
                      value={option.value}
                      checked={permission === option.value}
                      onChange={(e) => {
                        const v = e.target.value
                        const allowed: PermissionLevel[] = ['read','write','admin']
                        setPermission(allowed.includes(v as PermissionLevel) ? (v as PermissionLevel) : 'read')
                      }}
                      className="sr-only"
                    />
                    <Icon className={`w-5 h-5 ${option.color}`} />
                    <div className="flex-1">
                      <div className="font-medium text-gray-900 dark:text-white">
                        {option.label}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {option.description}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 mt-6">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleInvite}
            disabled={isLoading || !inviteValue.trim()}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <UserPlusIcon className="w-5 h-5" />
                Send Invitation
              </>
            )}
          </button>
        </div>
      </div>
      
      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={() => { setConfirmModal(prev => ({ ...prev, isOpen: false })) }}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText="Remove"
        cancelText="Cancel"
        variant="danger"
        isDangerous={true}
      />
    </div>
  );
};